import capitalize from 'lodash/capitalize';
import React from 'react';
import { match } from 'ts-pattern';

import { useCompleteFragment } from '@eluve/apollo-client';
import {
  HStack,
  Tag,
  TagProps,
  VStack,
  legacyTextStyles,
} from '@eluve/components';
import { formatDate } from '@eluve/date-utils';
import { graphql } from '@eluve/graphql.tada';

import { ArtifactHeader } from './ArtifactHeader';

export const patientConditionsFragment = graphql(`
  fragment PatientConditions on Patients @_unmask {
    __typename
    id
    conditions {
      __typename
      id
      severity
      recordedDate
      verificationStatus
      category
      code
      onsetDateTime
      note
    }
  }
`);

const ConditionBadge: React.FC<{ status: string | null }> = ({ status }) => {
  if (!status) {
    return null;
  }

  const color = match<string, TagProps['color']>(status)
    .with('provisional', () => 'purple')
    .with('confirmed', () => 'orange')
    .otherwise(() => 'gray');

  return <Tag pill size="s" color={color} label={capitalize(status)} />;
};

export interface PatientConditionsProps {
  patientId: string;
}

export const PatientConditions: React.FC<PatientConditionsProps> = ({
  patientId,
}) => {
  const { conditions } = useCompleteFragment({
    fragment: patientConditionsFragment,
    key: { id: patientId },
  });

  return (
    <VStack gap={3}>
      <ArtifactHeader text="Conditions" />
      <div className="grid w-full grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
        {conditions.map((c) => (
          <VStack key={c.id} className="rounded-lg border px-2 py-4">
            <VStack gap={0}>
              <HStack justify="between">
                <h4
                  className={legacyTextStyles.body({
                    size: 'l',
                  })}
                >
                  {c.code['text']}
                </h4>
                <ConditionBadge status={c.verificationStatus} />
              </HStack>
              <p
                className={legacyTextStyles.body({
                  size: 'm',
                  weight: 'regular',
                })}
              >
                {formatDate.toISO(new Date(c.onsetDateTime ?? ''))}
              </p>
            </VStack>
            <p>{c.note}</p>
          </VStack>
        ))}
      </div>
    </VStack>
  );
};
